import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { ArticleGetArticleQuery, ArticleGetArticleQueryVariables } from '@gql/graphql';
import { useMemo } from 'react';

export const ARTICLE_GET_ARTICLE = graphql(`
  query ArticleGetArticle(
    $where: ArticleWhereInput!
    $take: Int
    $translationsWhere: ArticleTranslationsWhereInput
    $takeArticles: Int
    $articlesWhere: ArticleWhereInput
    $articleCardInfoTranslationsWhere: ArticleTranslationsWhereInput
  ) {
    article: findManyArticle(where: $where, take: $take) {
      id
      reviewed
      coverImg
      createdAt
      authorName
      tags
      translations(where: $translationsWhere) {
        id
        url
        title
        content
        seoKeywords
        seoTitle
        seoDescription
        languageCode
      }
    }
    articles: findManyArticle(where: $articlesWhere, take: $takeArticles, randomOrder: true) {
      ...ArticleCardInfo
    }
  }
`);

export const useGetArticle = (id: string) => {
  const { languageCode } = useRouter();

  const variables = useMemo((): ArticleGetArticleQueryVariables => {
    return {
      take: 1,
      where: {
        translations: {
          some: {
            url: { equals: id },
            active: { equals: true },
            languageCode: { equals: languageCode },
          },
        },
      },
      translationsWhere: {
        AND: [
          { url: { not: { equals: '' } } },
          { url: { not: { equals: undefined } } },
          { title: { not: { equals: '' } } },
          { title: { not: { equals: undefined } } },
        ],
      },
      articleCardInfoTranslationsWhere: { languageCode: { equals: languageCode } },
      takeArticles: 8,
      articlesWhere: {
        AND: [
          {
            translations: {
              some: {
                active: { equals: true },
                languageCode: {
                  equals: languageCode,
                },
              },
            },
          },
          { published: { equals: true } },
        ],
      },
    };
  }, [id, languageCode]);

  const { data, error, loading } = useQuery<
    ArticleGetArticleQuery,
    ArticleGetArticleQueryVariables
  >(ARTICLE_GET_ARTICLE, { variables });

  return {
    article: data?.article?.[0],
    articles: data?.articles,
    articleError: error,
    articleLoading: loading,
  };
};
